import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";



const hints = new Map();
hints.set(DecodeHintType.TRY_HARDER, true);
hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.QR_CODE,
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_93,
    BarcodeFormat.ITF,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.PDF_417,
    BarcodeFormat.AZTEC,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
]);

// Scan every 200ms (default is 500ms) for faster detection
const codeReader = new BrowserMultiFormatReader(hints, 200);

const VideoStream = styled.video`
    margin: 0 auto,
    maxHeight: 80% ,
    opacity: 1,
`;

const ControlBar = styled.div`
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 12px;
    align-items: center;
    z-index: 10000;
`;

const CtrlButton = styled.button`
    background-color: rgba(0,0,0,0.55);
    color: white;
    border: 1.5px solid rgba(255,255,255,0.5);
    border-radius: 8px;
    padding: 6px 14px;
    font-size: 18px;
    cursor: pointer;
    user-select: none;
    &:active { opacity: 0.7; }
`;

const ZoomLabel = styled.span`
    color: white;
    font-size: 14px;
    min-width: 38px;
    text-align: center;
`;

export function BarcodeEngine({
    deviceId,
    timeout,
    playSound,
    onScanComplete,
    onTimeOut,
    isFrontCam
}) {
    timeout ||= 20000;

    const isCompleteRef = useRef(false);
    const isScanningRef = useRef(false);
    const videoTrackRef = useRef(null);

    const [torchOn, setTorchOn] = useState(false);
    const [torchAvailable, setTorchAvailable] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [zoomRange, setZoomRange] = useState({ min: 1, max: 1, step: 0.5 });

    const beepSound = useRef();
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const stopCameraStream = () => {
        BrowserMultiFormatReader.releaseAllStreams();
    };

    // Apply focus, torch, and zoom to the active video track
    const applyTrackConstraints = async (track, { torch, zoomValue }) => {
        try {
            const constraints = { advanced: [] };
            if (torch !== undefined) constraints.advanced.push({ torch });
            if (zoomValue !== undefined) constraints.advanced.push({ zoom: zoomValue });
            // Always request continuous autofocus
            constraints.advanced.push({ focusMode: 'continuous' });
            await track.applyConstraints(constraints);
        } catch {
            // Device may not support some constraints — ignore silently
        }
    };

    const toggleTorch = async () => {
        if (!videoTrackRef.current) return;
        const next = !torchOn;
        setTorchOn(next);
        await applyTrackConstraints(videoTrackRef.current, { torch: next, zoomValue: zoom });
    };

    const changeZoom = async (delta) => {
        if (!videoTrackRef.current) return;
        const next = Math.min(zoomRange.max, Math.max(zoomRange.min, parseFloat((zoom + delta).toFixed(1))));
        setZoom(next);
        await applyTrackConstraints(videoTrackRef.current, { torch: torchOn, zoomValue: next });
    };

    useEffect(() => {
        const previewElem = document.getElementById('video-preview');
        const startScanning = async () => {
            isScanningRef.current = true;

            const controls = await codeReader.decodeFromVideoDevice(deviceId, previewElem, async (result, error, controls) => {
                if (result) {
                    playSound && (beepSound.current.play());
                    const scannedValue = result.getText();
                    controls.stop();
                    await sleep(1000);

                    isCompleteRef.current = true;
                    isScanningRef.current = false;

                    onScanComplete && onScanComplete(scannedValue)

                }

                if (error) {
                    //console.log(error);
                }
            });

            // After ZXing attaches the stream, inspect track capabilities
            const stream = previewElem.srcObject;
            if (stream) {
                const track = stream.getVideoTracks()[0];
                if (track) {
                    videoTrackRef.current = track;

                    // Apply continuous autofocus immediately
                    await applyTrackConstraints(track, {});

                    // Detect torch support
                    const caps = track.getCapabilities ? track.getCapabilities() : {};
                    if (caps.torch) setTorchAvailable(true);

                    // Detect zoom range
                    if (caps.zoom) {
                        setZoomRange({
                            min: caps.zoom.min ?? 1,
                            max: caps.zoom.max ?? 1,
                            step: caps.zoom.step ?? 0.5,
                        });
                    }
                }
            }

            // stops scanning after timeout
            setTimeout(() => {
                if (isScanningRef.current) {
                    isScanningRef.current = false;
                    controls.stop();
                    if (!isCompleteRef.current) {
                        onTimeOut && onTimeOut();
                    }
                }
            }, timeout);
            return controls;
        };

        startScanning();
        return () => {
            stopCameraStream();
        };

    }, [codeReader, deviceId]);

    if (!deviceId)
        return null;

    const canZoom = zoomRange.max > zoomRange.min;

    return (
        <div style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        }}>
            <VideoStream id="video-preview" height="400" style={{
                transform: isFrontCam ? 'scaleX(-1)' : 'scaleX(1)'
            }} />
            <audio ref={beepSound} src="/scan_sound.mp3" preload="auto" />

            {/* <ControlBar>
                {torchAvailable && (
                    <CtrlButton onClick={toggleTorch} title="Senter">
                        {torchOn ? '🔦' : '💡'}
                    </CtrlButton>
                )}
                {canZoom && (
                    <>
                        <CtrlButton onClick={() => changeZoom(-zoomRange.step)} title="Zoom Out">−</CtrlButton>
                        <ZoomLabel>{zoom}×</ZoomLabel>
                        <CtrlButton onClick={() => changeZoom(zoomRange.step)} title="Zoom In">+</CtrlButton>
                    </>
                )}
            </ControlBar> */}
        </div>
    )
}


